import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import Sidebar from '../../components/layout/Sidebar';
import Header from '../../components/layout/Header';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Progress } from '../../components/ui/progress';
import {
  Coins,
  TrendingUp,
  TrendingDown,
  Sparkles,
  Wand2,
  Image,
  FileText,
  MessageSquare,
  Clock,
  ArrowUpRight,
  ArrowDownRight,
  Gift,
  Loader2,
  Zap
} from 'lucide-react';
import { cn } from '../../lib/utils';
import api from '../../lib/axios';
import { toast } from 'sonner';

const CreditsPage = () => {
  const { user, isAgency } = useAuth();
  const { theme } = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  
  const [credits, setCredits] = useState({
    balance: 0,
    total_earned: 0,
    total_spent: 0
  });
  
  const [history, setHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [creditsRes, historyRes] = await Promise.all([
        api.get('/credits/'),
        api.get('/credits/history')
      ]);
      setCredits(creditsRes.data || { balance: 0, total_earned: 0, total_spent: 0 });
      setHistory(historyRes.data || []);
    } catch (error) {
      console.error('Error fetching credits:', error);
    } finally {
      setLoading(false);
    }
  };

  const tools = [
    { id: 'ai_banner', name: 'Bannière IA', cost: 5, icon: Image, description: 'Générer une image de bannière' },
    { id: 'ai_text', name: 'Rédaction IA', cost: 2, icon: FileText, description: 'Générer du contenu texte' },
    { id: 'ai_chat', name: 'Assistant IA', cost: 1, icon: MessageSquare, description: 'Poser une question à l\'IA' },
    { id: 'ai_analysis', name: 'Analyse IA', cost: 3, icon: Sparkles, description: 'Analyser des données' }
  ];

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getToolIcon = (toolUsed) => {
    const tool = tools.find(t => t.id === toolUsed);
    return tool ? tool.icon : Zap;
  };

  return (
    <div className="min-h-screen bg-[#f8fafc]">
      <Sidebar collapsed={collapsed} setCollapsed={setCollapsed} mobileOpen={mobileOpen} setMobileOpen={setMobileOpen} />
      <Header collapsed={collapsed} setMobileOpen={setMobileOpen} />
      
      <main className={cn("transition-all duration-300 p-6 md:p-8 pt-24", collapsed ? "md:ml-20" : "md:ml-64")}>
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-3" style={{ color: theme.colors?.primary }}>
              <div className="w-10 h-10 rounded-xl flex items-center justify-center bg-gradient-to-br from-amber-400 to-orange-500">
                <Coins className="w-5 h-5 text-white" />
              </div>
              Suite MSC - Crédits
            </h1>
            <p className="text-[#64748b] mt-1">
              Gérez vos crédits pour les outils IA
            </p>
          </div>
          
          {isAgency && (
            <Button className="bg-gradient-to-r from-amber-500 to-orange-500 text-white">
              <Gift className="w-4 h-4 mr-2" />
              Acheter des crédits
            </Button>
          )}
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="w-8 h-8 animate-spin text-[#64748b]" />
          </div>
        ) : (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <Card className="card overflow-hidden">
                <div className="h-2 bg-gradient-to-r from-amber-400 to-orange-500" />
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Solde actuel</p>
                      <p className="text-4xl font-bold text-[#1a365d]">{credits.balance}</p>
                      <p className="text-xs text-[#64748b]">crédits disponibles</p>
                    </div>
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center">
                      <Coins className="w-8 h-8 text-amber-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Total gagné</p>
                      <p className="text-3xl font-bold text-green-600">+{credits.total_earned}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-green-100 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-[#64748b]">Total dépensé</p>
                      <p className="text-3xl font-bold text-red-500">-{credits.total_spent}</p>
                    </div>
                    <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center">
                      <TrendingDown className="w-6 h-6 text-red-500" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Tools */}
              <Card className="card lg:col-span-2">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Wand2 className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Outils disponibles
                  </CardTitle>
                  <CardDescription>
                    Utilisez vos crédits pour accéder aux outils IA
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {tools.map(tool => {
                      const Icon = tool.icon;
                      const canAfford = credits.balance >= tool.cost;
                      
                      return (
                        <div 
                          key={tool.id}
                          className={cn(
                            "p-4 rounded-xl border-2 transition-all",
                            canAfford 
                              ? "border-[#e2e8f0] hover:border-[var(--color-accent)] hover:shadow-md cursor-pointer" 
                              : "border-[#e2e8f0] opacity-50"
                          )}
                        >
                          <div className="flex items-start gap-4">
                            <div 
                              className="w-12 h-12 rounded-xl flex items-center justify-center"
                              style={{ backgroundColor: `${theme.colors?.accent}15` }}
                            >
                              <Icon className="w-6 h-6" style={{ color: theme.colors?.accent }} />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center justify-between mb-1">
                                <h4 className="font-semibold text-[#1a365d]">{tool.name}</h4>
                                <Badge className="bg-amber-100 text-amber-700">
                                  {tool.cost} crédits
                                </Badge>
                              </div>
                              <p className="text-sm text-[#64748b]">{tool.description}</p>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* History */}
              <Card className="card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <Clock className="w-5 h-5" style={{ color: theme.colors?.accent }} />
                    Historique
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {history.length === 0 ? (
                    <div className="text-center py-8 text-[#64748b]">
                      <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
                      <p>Aucune transaction</p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[400px] overflow-y-auto">
                      {history.map(transaction => {
                        const Icon = getToolIcon(transaction.tool_used);
                        const isEarn = transaction.amount > 0;
                        
                        return (
                          <div 
                            key={transaction.id}
                            className="flex items-center gap-3 p-3 rounded-lg bg-[#f8fafc]"
                          >
                            <div className={cn(
                              "w-10 h-10 rounded-lg flex items-center justify-center",
                              isEarn ? "bg-green-100" : "bg-red-100"
                            )}>
                              {isEarn ? (
                                <ArrowUpRight className="w-5 h-5 text-green-600" />
                              ) : (
                                <ArrowDownRight className="w-5 h-5 text-red-500" />
                              )}
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm text-[#1a365d] truncate">
                                {transaction.description}
                              </p>
                              <p className="text-xs text-[#94a3b8]">
                                {formatDate(transaction.created_at)}
                              </p>
                            </div>
                            <span className={cn(
                              "font-bold text-sm",
                              isEarn ? "text-green-600" : "text-red-500"
                            )}>
                              {isEarn ? '+' : ''}{transaction.amount}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </main>
    </div>
  );
};

export default CreditsPage;
